import { toast } from 'vue-sonner'
import { http as api } from '@/api/client'

const EXCEL_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const omitPagination = (params = {}) => {
  const { page, per_page, ...rest } = params
  void page
  void per_page
  return rest
}

/**
 * Pull filename from Content-Disposition when the API sends one.
 */
const filenameFromDisposition = (disposition) => {
  if (!disposition) return null

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim())
    } catch {
      return utf8Match[1].trim()
    }
  }

  const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
  return plainMatch?.[1]?.trim() || null
}

const triggerBlobDownload = (blob, filename) => {
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
}

/**
 * Parse an error payload when the API returns JSON wrapped as a blob.
 */
const readBlobErrorMessage = async (blob) => {
  try {
    const text = await blob.text()
    const json = JSON.parse(text)
    return json?.message || json?.errors?.[0] || null
  } catch {
    return null
  }
}

/**
 * Download an Excel export from a list endpoint's `/export` URL.
 * Passes current filters; omits page/per_page so the API can export the full filtered set.
 */
export const downloadExcelExport = async (
  url,
  { params = {}, filename = 'export.xlsx' } = {},
) => {
  try {
    const response = await api.get(url, {
      params: omitPagination(params),
      responseType: 'blob',
      ignoreGlobalCatch: true,
      headers: {
        Accept: `${EXCEL_MIME}, application/octet-stream, */*`,
      },
    })

    const blob = response.data
    const contentType = response.headers?.['content-type'] || ''

    if (contentType.includes('application/json')) {
      const message = (await readBlobErrorMessage(blob)) || 'Export failed'
      throw new Error(message)
    }

    const resolvedName =
      filenameFromDisposition(response.headers?.['content-disposition']) ||
      filename

    const fileBlob =
      blob instanceof Blob ? blob : new Blob([blob], { type: EXCEL_MIME })

    triggerBlobDownload(fileBlob, resolvedName)
    toast.success('Excel export downloaded')
    return 'success'
  } catch (error) {
    let message = error?.message || 'Failed to export Excel'

    if (error?.response?.data instanceof Blob) {
      message = (await readBlobErrorMessage(error.response.data)) || message
    } else if (error?.response?.data?.message) {
      message = error.response.data.message
    }

    toast.error(message)
    throw error
  }
}
